import jsPDF from "jspdf";
import { resolveMindsetBlocks } from "../data/mindset";

// Cached asset data URLs — fetched once per browser session.
const ASSETS = {
  logo: "/ewta_logo.png",
  breakfast: "/recipe_breakfast.jpg",
  lunch: "/recipe_lunch.jpg",
  dinner: "/recipe_dinner.jpg",
};
const cache = {};

async function loadDataUrl(path) {
  if (cache[path]) return cache[path];
  try {
    const res = await fetch(path);
    const blob = await res.blob();
    cache[path] = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    return cache[path];
  } catch (e) {
    return null;
  }
}

const GOLD = [212, 163, 115];
const INK = [43, 43, 43];
const INK_MUTED = [107, 107, 107];
const CREAM = [251, 245, 236];
const BORDER = [229, 225, 216];

export async function buildRoadmapPdf(data) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 48;
  const COL = W - M * 2;
  const FOOTER_RESERVE = 56;
  let y = 0;
  let pageNum = 1;

  const [logo, imgBreakfast, imgLunch, imgDinner] = await Promise.all([
    loadDataUrl(ASSETS.logo),
    loadDataUrl(ASSETS.breakfast),
    loadDataUrl(ASSETS.lunch),
    loadDataUrl(ASSETS.dinner),
  ]);

  const drawFooter = () => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...INK_MUTED);
    doc.text("HER ALIGNED  ·  Your Personalised Roadmap", M, H - 24);
    doc.text("Copyright EWTA 2026", W / 2, H - 24, { align: "center" });
    doc.text(String(pageNum), W - M, H - 24, { align: "right" });
  };
  const drawHeaderStrip = () => {
    doc.setFillColor(...CREAM);
    doc.rect(0, 0, W, 76, "F");
    // Full logo, square box (asset is 1:1) vertically centred in the 76pt strip — never cropped.
    if (logo) { try { doc.addImage(logo, "PNG", M, 14, 48, 48); } catch (e) { /* logo optional */ } }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...INK_MUTED);
    doc.text("HER ALIGNED", M + 60, 36, { charSpace: 1.5 });
    doc.setFont("times", "italic");
    doc.setFontSize(13);
    doc.setTextColor(...INK);
    doc.text("Your Personalised Roadmap", M + 60, 54);
    doc.setDrawColor(...GOLD);
    doc.setLineWidth(0.8);
    doc.line(M, 76, W - M, 76);
  };
  const newPage = () => { drawFooter(); doc.addPage(); pageNum += 1; y = M; drawHeaderStrip(); y = 120; };
  const ensureSpace = (need) => { if (y + need > H - FOOTER_RESERVE) newPage(); };
  // Apply a consistent text style. Call this BEFORE every doc.text() inside a
  // loop so that font drift after a page break (drawHeaderStrip switches
  // styles) cannot leak into the caller's intended formatting.
  const setText = (family, style, size, color = INK) => {
    doc.setFont(family, style);
    doc.setFontSize(size);
    doc.setTextColor(...color);
  };

  const sectionHeader = (number, title) => {
    // A major section heading must not orphan at the bottom of a page. We
    // require enough room for the header itself PLUS the typical first
    // block beneath it (target-box ≈ 64pt, or first paragraph ≈ 60pt).
    ensureSpace(52 + 80);
    doc.setFont("times", "bold");
    doc.setFontSize(46);
    doc.setTextColor(...GOLD);
    doc.text(number, M, y + 24);
    doc.setFont("times", "normal");
    doc.setFontSize(22);
    doc.setTextColor(...INK);
    doc.text(title, M + 64, y + 18);
    y += 52;
  };
  const renderTargetBox = (label, value, x, w, opts = {}) => {
    const valFontSize = opts.small ? 11 : 18;
    const lh = opts.small ? 14 : 22;
    const valueLines = doc.splitTextToSize(value || "", w - 28);
    const boxH = Math.max(64, 38 + valueLines.length * lh);
    ensureSpace(boxH + 4);
    doc.setFillColor(...CREAM);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.6);
    doc.roundedRect(x, y, w, boxH, 10, 10, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...INK_MUTED);
    doc.text(label.toUpperCase(), x + 16, y + 22, { charSpace: 1.2 });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(valFontSize);
    doc.setTextColor(...INK);
    let vy = y + 44;
    valueLines.forEach((ln) => { doc.text(ln, x + 16, vy); vy += lh; });
    return boxH;
  };
  const renderTwoColumnTargets = (a, b) => {
    const gap = 12;
    const w = (COL - gap) / 2;
    const yStart = y;
    const hA = renderTargetBox(a.label, a.value, M, w, a.opts);
    y = yStart;
    const hB = renderTargetBox(b.label, b.value, M + w + gap, w, b.opts);
    y = yStart + Math.max(hA, hB) + 16;
  };
  const renderParagraph = (str, opts = {}) => {
    const size = opts.size || 10.5;
    const color = opts.muted ? INK_MUTED : INK;
    const lh = opts.lineHeight || 15;
    setText("helvetica", "normal", size, color);
    const lines = doc.splitTextToSize(str || "", COL);
    lines.forEach((ln) => {
      ensureSpace(lh);
      setText("helvetica", "normal", size, color);
      doc.text(ln, M, y);
      y += lh;
    });
    y += opts.spaceAfter == null ? 10 : opts.spaceAfter;
  };
  // ===== Measurement helpers (used to keep logical blocks together so a
  // sub-heading, bullet list, numbered step or whole recipe block never
  // straddles a page break) =====
  const measureLines = (str, width, font = ["helvetica", "normal"], size = 10.5) => {
    doc.setFont(font[0], font[1]);
    doc.setFontSize(size);
    return doc.splitTextToSize(str || "", width).length;
  };
  const measureBullets = (items, width = COL - 18) => {
    let h = 0;
    items.forEach((it) => {
      h += measureLines(it, width) * 14 + 2;
    });
    return h + 6;
  };
  const measureNumberedSteps = (items, width = COL - 22) => {
    let h = 0;
    items.forEach((it) => {
      h += measureLines(it, width) * 14 + 2;
    });
    return h + 6;
  };
  const measureSubHeading = (label, opts = {}) => {
    const size = opts.size || 13;
    const lines = measureLines(label, COL, ["times", "bold"], size);
    return lines * (size + 4) + 4;
  };
  // Compute the "lock-together" height of a recipe header: eyebrow + name
  // + macros + caption + image. We use this to force the entire visual
  // header block to start on a fresh page when it would otherwise be split.
  const recipeHeaderHeight = (RECIPE_IMG_H) => 16 + 18 + 16 + 16 + 4 + RECIPE_IMG_H + 14;

  const renderSubHeading = (label, opts = {}) => {
    const size = opts.size || 13;
    const headH = measureSubHeading(label, { size });
    // Orphan protection: a sub-heading must always have at least one full
    // line of content room beneath it before a page break. If only the
    // heading would fit, push it AND its content to a new page.
    const needWithFirstLine = headH + 16;
    ensureSpace(needWithFirstLine);
    setText("times", "bold", size, INK);
    const lines = doc.splitTextToSize(label, COL);
    lines.forEach((ln) => {
      ensureSpace(size + 4);
      setText("times", "bold", size, INK);
      doc.text(ln, M, y);
      y += size + 4;
    });
    y += 4;
  };
  const renderHabitColumn = (heading, habits) => {
    ensureSpace(40);
    setText("helvetica", "bold", 8.5, GOLD);
    doc.text(heading.toUpperCase(), M, y, { charSpace: 1.4 });
    y += 16;
    (habits || []).forEach((h) => {
      setText("times", "bold", 13, INK);
      const titleLines = doc.splitTextToSize(h.title || "", COL - 24);
      setText("helvetica", "normal", 10, INK_MUTED);
      const explLines = doc.splitTextToSize(h.explanation || "", COL - 24);
      const cardH = 16 + titleLines.length * 16 + explLines.length * 14 + 18;
      ensureSpace(cardH + 8);
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(...BORDER);
      doc.setLineWidth(0.6);
      doc.roundedRect(M, y, COL, cardH, 10, 10, "FD");
      let ty = y + 24;
      titleLines.forEach((ln) => {
        setText("times", "bold", 13, INK);
        doc.text(ln, M + 14, ty);
        ty += 16;
      });
      let ey = ty + 4;
      explLines.forEach((ln) => {
        setText("helvetica", "normal", 10, INK_MUTED);
        doc.text(ln, M + 14, ey);
        ey += 14;
      });
      y += cardH + 10;
    });
  };
  const renderBullets = (items) => {
    items.forEach((item) => {
      setText("helvetica", "normal", 10.5, INK);
      const lines = doc.splitTextToSize(item, COL - 18);
      // Keep all wrapped lines of a single bullet together so a multi-line
      // item never gets split across pages mid-thought.
      const itemH = lines.length * 14 + 2;
      ensureSpace(itemH);
      lines.forEach((ln, i) => {
        if (i === 0) {
          setText("helvetica", "normal", 10.5, GOLD);
          doc.text("\u2022", M + 2, y);
        }
        setText("helvetica", "normal", 10.5, INK);
        doc.text(ln, M + 16, y);
        y += 14;
      });
      y += 2;
    });
    y += 6;
  };
  const renderNumberedSteps = (items) => {
    items.forEach((item, idx) => {
      setText("helvetica", "normal", 10.5, INK);
      const lines = doc.splitTextToSize(item, COL - 22);
      // Keep the full numbered step together (number + every wrapped line)
      // so we never end up with the marker on one page and the text on the
      // next, or step text split mid-sentence across pages.
      const itemH = lines.length * 14 + 2;
      ensureSpace(itemH);
      lines.forEach((ln, i) => {
        if (i === 0) {
          setText("helvetica", "bold", 10.5, GOLD);
          doc.text(`${idx + 1}.`, M + 2, y);
        }
        setText("helvetica", "normal", 10.5, INK);
        doc.text(ln, M + 20, y);
        y += 14;
      });
      y += 2;
    });
    y += 6;
  };
  const RECIPE_IMG_H = 150; // ~17% shorter than the previous 180pt — frees
                            // vertical room for cleaner pagination and means
                            // the (now upscaled) source images render sharper.
  const renderRecipeTitle = (label, name) => {
    // Keep the eyebrow + dish name together. We don't ensureSpace for the
    // entire recipe header here — that's the caller's job via
    // ensureRecipeHeaderRoom() — but we DO guarantee the eyebrow never sits
    // alone at the very bottom of a page.
    ensureSpace(16 + 18 + 16);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...GOLD);
    doc.text(label.toUpperCase(), M, y, { charSpace: 1.6 });
    y += 16;
    doc.setFont("times", "bold");
    doc.setFontSize(15);
    doc.setTextColor(...INK);
    doc.text(name, M, y);
    y += 18;
  };
  const renderMacroLine = (cals, protein) => {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(...INK_MUTED);
    doc.text(`Calories ${cals}  |  Protein ${protein}g`, M, y);
    y += 16;
  };
  const renderMutedCaption = (str) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...INK_MUTED);
    doc.text(str, M, y);
    y += 16;
  };
  const renderTotalCallout = (str) => {
    const boxH = 56;
    // The DAY TOTAL box must NEVER be split across pages, so reserve the
    // full box plus its bottom margin in one ensureSpace call.
    ensureSpace(boxH + 18);
    doc.setFillColor(...CREAM);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.6);
    doc.roundedRect(M, y, COL, boxH, 10, 10, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...INK_MUTED);
    doc.text("DAY TOTAL", M + 16, y + 22, { charSpace: 1.6 });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(...INK);
    doc.text(str, M + 16, y + 42);
    y += boxH + 14;
  };
  // A "major section" should never begin in the bottom third of a page —
  // otherwise the heading + summary line orphan above a fresh page of
  // content. 320pt covers a section header (~70pt) + the first meaningful
  // block beneath it (paragraph, target box, or first habit card).
  const startNewPageForSection = (need = 320) => {
    if (y + need > H - FOOTER_RESERVE) newPage();
  };
  const renderRecipePhoto = (img) => {
    if (!img) return;
    const targetW = COL;
    const targetH = RECIPE_IMG_H;
    ensureSpace(targetH + 14);
    // 'SLOW' compression is the highest-quality jsPDF mode (smallest pixel
    // loss when re-encoding). The source JPEGs have also been upscaled to
    // 1400px wide, so the rendered image stays sharp at 200%+ PDF zoom.
    try {
      doc.addImage(img, "JPEG", M, y, targetW, targetH, undefined, "SLOW");
    } catch (e) {
      try { doc.addImage(img, "JPEG", M, y, targetW, targetH); } catch (_e) { /* optional */ }
    }
    y += targetH + 14;
  };
  // Lock-together helper: ensure the whole recipe HEADER (eyebrow + name +
  // macros + caption + photo + "Ingredients" sub-head + first ingredient)
  // fits on the current page. If not, jump to a fresh page first.
  const ensureRecipeHeaderRoom = () => {
    const lockHeight = recipeHeaderHeight(RECIPE_IMG_H) + measureSubHeading("Ingredients", { size: 11 }) + 16;
    ensureSpace(lockHeight);
  };

  // ===== Cover =====
  drawHeaderStrip();
  y = 130;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...GOLD);
  doc.text("YOUR PERSONALISED ROADMAP", M, y, { charSpace: 2 });
  y += 28;
  doc.setFont("times", "normal");
  doc.setFontSize(28);
  doc.setTextColor(...INK);
  const firstName = ((data.answers && data.answers.name) || "").trim().split(/\s+/)[0] || "you";
  const greeting = doc.splitTextToSize(
    `Congratulations ${firstName}! Here is your Personalised Roadmap.`,
    COL
  );
  greeting.forEach((ln) => { doc.text(ln, M, y); y += 32; });
  y += 12;
  renderParagraph(
    "This roadmap is your custom made clear path forwards to not only getting the results you want but becoming the woman who can keep them long term."
  );
  renderParagraph("Take your time to read everything.");
  renderParagraph(
    "If you have any questions or want a clear timeframe on how long you can expect to reach your goals and build the habits and skills to keep them, simply message Justine in Everfit."
  );

  // ===== 01 Nutrition =====
  sectionHeader("01", "Your Nutrition Targets");
  renderTwoColumnTargets(
    { label: "Starting Calorie Range", value: data.calorie_range },
    { label: "Starting Protein Range", value: data.protein_range }
  );
  renderParagraph(data.nutrition_advice);

  // ===== 02 Fitness =====
  newPage();
  sectionHeader("02", "Your Fitness Targets");
  renderTwoColumnTargets(
    { label: "Daily Step Range", value: data.step_range },
    { label: "Training Frequency", value: data.training_frequency, opts: { small: true } }
  );
  renderParagraph(data.fitness_advice);

  // ===== 03 Wellbeing =====
  newPage();
  sectionHeader("03", "Your Wellbeing");
  renderHabitColumn("Sleep habits to focus on", data.sleep_habits);
  y += 4;
  renderHabitColumn("Stress regulation habits", data.stress_habits);

  // ===== 04 Mindset (deterministic, driven by Q13 + Q14 selections) =====
  const { patternBlocks, rootBlock } = resolveMindsetBlocks(
    data.answers && data.answers.mindset_patterns,
    data.answers && data.answers.mindset_root,
  );
  if (patternBlocks.length || rootBlock) {
    newPage();
    sectionHeader("04", "Your Mindset Roadmap");

    // Measure a mindset block so we can force a new page when the WHOLE block
    // would otherwise get split across a page break. Only force a new page
    // when the block will actually fit on a fresh page (very long variants
    // must still flow naturally to avoid infinite empty-page loops).
    const measureMindsetBlock = (block) => {
      let h = measureSubHeading(block.title, { size: 14 });
      (block.paragraphs || []).forEach((p) => {
        h += measureLines(p, COL) * 15 + 8;
      });
      if (block.bullets && block.bullets.length) {
        h += measureBullets(block.bullets);
      }
      (block.outroParagraphs || []).forEach((p) => {
        h += measureLines(p, COL) * 15 + 8;
      });
      return h + 10;
    };
    const availableOnFreshPage = H - FOOTER_RESERVE - 120;

    const renderMindsetBlock = (block) => {
      // Keep the whole guidance block together where possible so a
      // sub-heading + its paragraphs / bullets never get split mid-idea.
      const blockH = measureMindsetBlock(block);
      const remaining = H - FOOTER_RESERVE - y;
      if (blockH > remaining && blockH <= availableOnFreshPage) {
        newPage();
      }
      renderSubHeading(block.title, { size: 14 });
      (block.paragraphs || []).forEach((p) => renderParagraph(p, { spaceAfter: 8 }));
      if (block.bullets && block.bullets.length) {
        renderBullets(block.bullets);
      }
      (block.outroParagraphs || []).forEach((p) => renderParagraph(p, { spaceAfter: 8 }));
      y += 10;
    };

    patternBlocks.forEach((b) => renderMindsetBlock(b));
    if (rootBlock) {
      renderMindsetBlock(rootBlock);
    }
  }

  // ===== 05 Understanding =====
  newPage();
  sectionHeader("05", "Understanding Your Roadmap for Optimal Results");
  renderParagraph(
    "Your personalised roadmap has now given you the personalised targets that are going to move the needle the most for you to get the results you want, based on your body, your goals and your daily habits. So you have clarity on exactly how to implement this to get the best results in the most enjoyable way, focus on these 3 things first and build as you go."
  );
  renderSubHeading("1. Track your calories in real time and create consistency with your meals and meal timing.");
  renderParagraph(
    "Being consistent with your calorie deficit is going to be the most important habit for your fat loss. Use an app like MyFitnessPal, or even Google AI or ChatGPT to take photos of your food and ask for calorie and protein estimation. The goal of tracking is not 100% accuracy, it's to create nutritional awareness around your food habits and choices so you can make empowered choices around food that give you both the fat loss results you want, whilst enjoying your food and social life at the same time."
  );
  renderSubHeading("2. Track your steps");
  renderParagraph(
    "Steps fall into NEAT (Non-Exercise Activity Thermogenesis), which takes up roughly 20-25% of our daily energy expenditure. This is why for your fitness targets you should focus on consistently hitting your daily step targets as a priority first, before you start trying to increase your training sessions."
  );
  renderParagraph(
    "Your training sessions only take up 5-10% of your total daily energy expenditure. Which is why new clients ask me all the time, \"Justine, I'm training 5-6 days a week but not losing weight! What gives?!\" It's because the 2 most important drivers for fat loss are calories and steps."
  );
  renderParagraph(
    "This is not to say training isn't important - it is. But your training sessions are where you go to build strength, build muscle, improve fitness and cardio health, and set specific performance goals. They're not where you go to lose fat."
  );
  renderSubHeading("3. Improving your sleep");
  renderParagraph(
    "Studies show that just getting 1 hour less sleep a night can increase our food consumption by up to 500 calories a day, and we do this unconsciously. Sleep has a direct impact on our hunger hormones, our energy levels and our mood. It is the MVP habit that upholds your capacity and ability to hit your nutrition and fitness goals on any given day. That's why you've received 4 support habits to help you manage your stress and sleep so you can improve it ASAP. You don't need to implement all 4 - pick the one you know is going to benefit you the most, or jump into our community and ask Justine what she'd recommend for you."
  );
  renderParagraph(
    "We realise your Personalised Roadmap has given you a lot of information, and that's deliberate to ensure you have 100% clarity to get started. We always want to overdeliver for every one of our clients. Take your time to process your roadmap and drop any questions in our community or ask them on the next live call.",
    { muted: true }
  );

  drawFooter();
  return doc;
}

export async function pdfBlob(data) {
  const doc = await buildRoadmapPdf(data);
  return doc.output("blob");
}
